import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useToast,
  Checkbox,
  Box,
  Text,
  Spinner,
  VStack,
  Skeleton,
} from '@chakra-ui/react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { createSignedDocument, fetchDocument, signDocumentDigitally } from '../../api/document/document'
import UploadedFile from '../display/UploadedFile'
import handleToast from '../HandleToast'
import FileUpload from '../input/FileUpload'
import DocumentViewer from '../display/DocumentViewer'
import { useTranslation } from 'react-i18next'
import { modal, button } from '../../themes/styles'

interface AddSignedDocumentModalProps {
  isOpen: boolean
  onClose: () => void
  document: string
  onSuccess?: () => void
}

const AddSignedDocumentModal: React.FC<AddSignedDocumentModalProps> = ({ isOpen, onClose, document, onSuccess }) => {
  const { t } = useTranslation()
  const toast = useToast()
  const queryClient = useQueryClient()

  const [data, setData] = useState({
    file: null as File | null,
  })

  const [errors, setErrors] = useState({
    file: false,
  })

  // Fetch the document to determine if it's a digital signature flow
  const documentQuery = useQuery({
    queryKey: ['document', document],
    queryFn: () => fetchDocument(document, { fields: 'status' }),
    enabled: !!document && isOpen,
  })

  const isDigitalSignature = useMemo(() => {
    const status = documentQuery.data?.status
    return status === 'pending_digital_signature'
  }, [documentQuery.data?.status])

  const isDocumentReady = !documentQuery.isLoading && !documentQuery.isFetching && documentQuery.data != null

  const [acknowledged, setAcknowledged] = useState(false)

  const validateData = () => {
    const errors = {
      file: !data.file,
    }

    setErrors(errors)

    return !Object.values(errors).some((error) => error)
  }

  const createSignedDocumentMutation = useMutation({
    mutationFn: createSignedDocument,
    onSuccess: () => {
      handleToast(toast, true, t('documents.toasts.uploadedTitle'), t('documents.toasts.uploadedDescription'))
      queryClient.invalidateQueries({ queryKey: ['documents'] })
      if (document) queryClient.invalidateQueries({ queryKey: ['document', document] })
      handleClose()
      
      // Call onSuccess callback if provided
      if (onSuccess) {
        onSuccess()
      }
    },
    onError: (error: Error) => {
      handleToast(toast, false, t('documents.toasts.uploadFailedTitle'), error.message)
    },
  })

  // Mutation for digital signature acknowledgment (no file upload)
  const acknowledgeDigitalSignature = useMutation({
    mutationFn: () => signDocumentDigitally(document),
    onSuccess: () => {
      handleToast(toast, true, t('documents.toasts.signedTitle'), t('documents.toasts.signedDescription'))
      queryClient.invalidateQueries({ queryKey: ['documents'] })
      if (document) queryClient.invalidateQueries({ queryKey: ['document', document] })
      handleClose()
      if (onSuccess) onSuccess()
    },
    onError: (error: Error) => {
      handleToast(toast, false, t('documents.toasts.digitalSigningFailedTitle'), error.message)
    },
  })

  const handleSubmit = () => {
    if (isDigitalSignature) {
      if (!acknowledged) {
        handleToast(
          toast,
          false,
          t('documents.modals.signed.acknowledgementRequiredTitle'),
          t('documents.modals.signed.acknowledgementRequiredDescription')
        )
        return
      }
      acknowledgeDigitalSignature.mutate()
    } else {
      if (!validateData()) return
      createSignedDocumentMutation.mutate({
        file: data.file as File,
        id: document ?? '',
      })
    }
  }
  const handleClose = () => {
    setData({
      file: null,
    })
    setAcknowledged(false)
    onClose()
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} isCentered size="lg">
      <ModalOverlay />
      <ModalContent minW="640px">
        <ModalHeader {...modal.header}>
          {!isDocumentReady
            ? t('documents.modals.signed.loadingTitle', 'Sign document')
            : isDigitalSignature
              ? t('documents.modals.signed.reviewAndSignTitle')
              : t('documents.modals.signed.uploadSignedTitle')}
        </ModalHeader>

        <ModalCloseButton sx={modal.closeButton} />

        <ModalBody minH="280px">
          {!isDocumentReady ? (
            <VStack align="stretch" spacing={4} py={2}>
              <Box display="flex" alignItems="center" justifyContent="center" minH="220px" w="100%" bg="solid.25" borderRadius="12px">
                <VStack spacing={4}>
                  <Spinner size="lg" color="primary.100" thickness="3px" />
                  <Text fontSize="14px" color="solid.500" fontWeight="500">
                    {t('documents.modals.signed.loadingDescription', 'Loading document...')}
                  </Text>
                </VStack>
              </Box>
              <Skeleton height="20px" borderRadius="8px" startColor="solid.100" endColor="solid.50" />
              <Skeleton height="20px" width="80%" borderRadius="8px" startColor="solid.100" endColor="solid.50" />
            </VStack>
          ) : isDigitalSignature ? (
            documentQuery.data ? (
              <>
                <Box mb={4}>
                  <DocumentViewer documentId={document} useDocumentQuery={true} readOnly={true} />
                </Box>
                <Checkbox isChecked={acknowledged} onChange={(e) => setAcknowledged(e.target.checked)}>
                  {t('documents.modals.signed.acknowledgementText')}
                </Checkbox>
              </>
            ) : (
              <Text textAlign="center" color="solid.500">{t('documents.modals.signed.previewError')}</Text>
            )
          ) : !data.file ? (
            <FileUpload
              setFile={(file) => setData((prev) => ({ ...prev, file: file as File }))}
              label={t('documents.modals.signed.selectFileLabel')}
              fileLabel={t('documents.modals.signed.fileLabel')}
              detail={t('documents.modals.signed.fileDetail')}
              isInvalid={!!errors.file}
            />
          ) : (
            <UploadedFile
              file={data.file as File}
              onRemove={() => setData((prev) => ({ ...prev, file: null }))}
              label={t('documents.modals.signed.selectFileLabel')}
              isLoading={createSignedDocumentMutation.isPending}
            />
          )}
        </ModalBody>
        <ModalFooter {...modal.footer}>
          <Button {...button.secondary} onClick={handleClose}>
            {t('common.actions.cancel')}
          </Button>
          <Button
            {...button.primary}
            onClick={handleSubmit}
            isLoading={isDigitalSignature ? acknowledgeDigitalSignature.isPending : createSignedDocumentMutation.isPending}
            isDisabled={!isDocumentReady || (isDigitalSignature && !acknowledged)}
          >
            {isDigitalSignature ? t('documents.actions.sign') : t('common.actions.submit')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default AddSignedDocumentModal
